import React, { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Upload, Image as ImageIcon, Sparkles, X } from "lucide-react";

export default function ImagePicker({ value, onChange, testIdPrefix = "img" }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("upload");
  const [uploading, setUploading] = useState(false);
  const [stockQ, setStockQ] = useState("atlanta home");
  const [stockResults, setStockResults] = useState([]);
  const [stockBusy, setStockBusy] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiBusy, setAiBusy] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Use the long-lived signed URL returned by the backend so the image
      // stays reachable for the lifetime of the record (365 days) instead of
      // relying on the 12-hour login JWT.
      const full = data.url.startsWith("http")
        ? data.url
        : `${window.location.protocol}//${window.location.host}${data.url}`;
      onChange(full);
      toast.success("Uploaded");
      setOpen(false);
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const searchStock = async () => {
    setStockBusy(true);
    try {
      const { data } = await api.get("/stock/search", { params: { q: stockQ } });
      setStockResults(data.results || []);
    } catch { toast.error("Stock search failed"); }
    finally { setStockBusy(false); }
  };

  const genAI = async () => {
    if (!aiPrompt.trim()) return toast.error("Describe the image first");
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/image", { prompt: aiPrompt });
      const full = data.url.startsWith("http") ? data.url : `${window.location.protocol}//${window.location.host}${data.url}`;
      onChange(full);
      toast.success("AI image generated");
      setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "AI image failed"); }
    finally { setAiBusy(false); }
  };

  const pickStock = (url) => { onChange(url); setOpen(false); toast.success("Stock image chosen"); };

  return (
    <div data-testid={`${testIdPrefix}-picker`}>
      {value && (
        <div className="mb-2 flex items-start gap-3">
          <img src={value} alt="" className="w-32 h-20 object-cover rounded-md border border-[#d1cece]" />
          <div className="flex flex-col gap-1">
            <button type="button" onClick={() => setOpen(true)} className="text-xs uppercase tracking-wider text-[#bf858e] hover:text-[#050303]" data-testid={`${testIdPrefix}-change`}>Change</button>
            <button type="button" onClick={() => onChange("")} className="text-xs uppercase tracking-wider text-[#050303]/40 hover:text-[#bf858e]" data-testid={`${testIdPrefix}-clear`}>Remove</button>
          </div>
        </div>
      )}
      {!value && (
        <button type="button" onClick={() => setOpen(true)}
          className="text-xs font-semibold text-[#bf858e] hover:text-[#050303] flex items-center gap-1"
          data-testid={`${testIdPrefix}-open`}>
          <ImageIcon size={13} /> Add image
        </button>
      )}

      {open && (
        <div className="fixed inset-0 bg-[#050303]/70 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-lg w-full max-w-2xl shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid={`${testIdPrefix}-modal`}>
            <div className="flex items-center justify-between p-4 border-b border-[#d1cece]">
              <h3 className="font-heading text-xl">Add Image</h3>
              <button onClick={() => setOpen(false)} className="text-[#050303]/60 hover:text-[#050303]"><X size={18} /></button>
            </div>
            <div className="flex border-b border-[#d1cece]">
              <TabBtn active={tab === "upload"} onClick={() => setTab("upload")} icon={<Upload size={13} />} label="Upload" test={`${testIdPrefix}-tab-upload`} />
              <TabBtn active={tab === "stock"} onClick={() => setTab("stock")} icon={<ImageIcon size={13} />} label="Stock" test={`${testIdPrefix}-tab-stock`} />
              <TabBtn active={tab === "ai"} onClick={() => setTab("ai")} icon={<Sparkles size={13} />} label="AI Generate" test={`${testIdPrefix}-tab-ai`} />
            </div>
            <div className="p-6 min-h-[240px]">
              {tab === "upload" && (
                <div>
                  <label className="block border-2 border-dashed border-[#d1cece] rounded-lg p-8 text-center cursor-pointer hover:bg-[#ffffff]" data-testid={`${testIdPrefix}-upload-drop`}>
                    <Upload size={24} className="mx-auto text-[#bf858e] mb-2" />
                    <div className="text-sm font-semibold">Choose an image from your device</div>
                    <div className="text-xs text-[#050303]/55 mt-1">PNG, JPG, WEBP up to a few MB</div>
                    <input type="file" accept="image/*" onChange={upload} className="hidden" data-testid={`${testIdPrefix}-upload-input`} />
                  </label>
                  {uploading && <div className="text-xs text-[#050303]/60 mt-3 text-center">Uploading…</div>}
                </div>
              )}

              {tab === "stock" && (
                <div>
                  <div className="flex gap-2">
                    <input value={stockQ} onChange={e => setStockQ(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && searchStock()}
                      placeholder="Search (e.g. Atlanta home, front porch, family)"
                      className="flex-1 px-3 py-2 border border-[#d1cece] rounded-md text-sm"
                      data-testid={`${testIdPrefix}-stock-q`} />
                    <button onClick={searchStock} disabled={stockBusy}
                      className="bg-[#050303] text-white pill-btn text-sm disabled:opacity-50"
                      data-testid={`${testIdPrefix}-stock-search`}>{stockBusy ? "…" : "Search"}</button>
                  </div>
                  <div className="grid grid-cols-3 gap-3 mt-4">
                    {stockResults.map((u, i) => (
                      <button key={u} type="button" onClick={() => pickStock(u)}
                        className="aspect-video rounded-md overflow-hidden border border-[#d1cece] hover:ring-2 hover:ring-[#bf858e] transition-all"
                        data-testid={`${testIdPrefix}-stock-${i}`}>
                        <img src={u} alt="" className="w-full h-full object-cover" loading="lazy" />
                      </button>
                    ))}
                  </div>
                  {stockResults.length === 0 && !stockBusy && (
                    <div className="text-xs text-[#050303]/55 mt-6 text-center">Type a keyword and hit Search — results powered by Unsplash.</div>
                  )}
                </div>
              )}

              {tab === "ai" && (
                <div>
                  <div className="overline mb-2">Powered by Gemini Nano Banana</div>
                  <textarea value={aiPrompt} onChange={e => setAiPrompt(e.target.value)}
                    rows={3}
                    placeholder="e.g. Warm, editorial photo of a Snellville craftsman-style home at golden hour with a rose garden"
                    className="w-full p-3 border border-[#d1cece] rounded-lg text-sm"
                    data-testid={`${testIdPrefix}-ai-prompt`} />
                  <button onClick={genAI} disabled={aiBusy || !aiPrompt.trim()}
                    className="mt-3 bg-[#050303] text-white pill-btn text-sm flex items-center gap-2 disabled:opacity-50"
                    data-testid={`${testIdPrefix}-ai-gen`}>
                    <Sparkles size={14} /> {aiBusy ? "Generating…" : "Generate image"}
                  </button>
                  <div className="text-xs text-[#050303]/50 mt-3">Generation typically takes 5-15 seconds.</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TabBtn({ active, onClick, icon, label, test }) {
  return (
    <button onClick={onClick} data-testid={test}
      className={`flex-1 py-3 text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${
        active ? "bg-white text-[#050303] border-b-2 border-[#bf858e]" : "bg-[#ffffff] text-[#050303]/55 hover:text-[#050303]"
      }`}>
      {icon} {label}
    </button>
  );
}
